import {
  FoodMakerMenu,
  ProductDeliveryConditions,
  getProductDeliveryConditions,
} from './contexts/MenuData';

export const DEFAULT_AVERAGE_DELIVERY_MINUTES = 40;
export const FULFILMENT_SCHEDULE_HORIZON_DAYS = 14;

export type DeliveryScheduleTimeSlot = [string, string];

export type DeliveryScheduleDayMatch = {
  allDay: boolean;
  slots: DeliveryScheduleTimeSlot[];
};

export type ScheduledIntervalCandidate = {
  dateValue: string;
  label: string;
  start: Date;
  end: Date;
};

export type CartFulfilmentConstraints = {
  preparationMinutes: number;
  nearestInterval: number | null;
};

export type FulfilmentMode = 'delivery' | 'pickup';

export type FulfilmentScheduleResolution = {
  readinessTime: Date;
  eligibleIntervals: ScheduledIntervalCandidate[];
  firstAllowedInterval: ScheduledIntervalCandidate | null;
  allowedIntervals: ScheduledIntervalCandidate[];
};

export type ResolvedLocationSchedule<T> = {
  locationCode: string;
  entry: T;
};

type CartProductReference = {
  product_key: string;
};

const isUnknownRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const hasRecordKey = (record: Record<string, unknown>, key: string): boolean => (
  Object.prototype.hasOwnProperty.call(record, key)
);

/**
 * Finds the active location schedule in the backend-defined fallback order.
 * Each requested location is tried independently before the country-wide and
 * partner-location entries, so one unknown order location cannot hide a valid
 * session or user location.
 */
export const resolveLocationSchedule = <T>(
  deliverySchedule: Record<string, T> | null | undefined,
  requestedLocations: Array<string | null | undefined>,
  partnerLocation?: string | null,
): ResolvedLocationSchedule<T> | null => {
  if (!deliverySchedule) {
    return null;
  }

  const candidateLocations = [
    ...requestedLocations,
    'Whole_country',
    partnerLocation,
  ].filter((location, index, locations): location is string => (
    typeof location === 'string'
    && location.trim().length > 0
    && locations.indexOf(location) === index
  ));

  for (const locationCode of candidateLocations) {
    const entry = deliverySchedule[locationCode];
    if (entry) {
      return { locationCode, entry };
    }
  }

  return null;
};

/**
 * Converts backend time strings into minutes while accepting the historical
 * `19.30` representation used by some partner schedules.
 */
export const toTimeInMinutes = (value: string | null | undefined): number | null => {
  if (!value) {
    return null;
  }

  const normalizedValue = value.trim().replace('.', ':');
  const match = /^(\d{1,2}):(\d{2})$/.exec(normalizedValue);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return (hours * 60) + minutes;
};

export const normalizeTimeLabel = (value: string): string => {
  const minutes = toTimeInMinutes(value);
  if (minutes == null) {
    return value;
  }
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
};

export const formatDeliveryDateValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const dateAtTime = (date: Date, time: string): Date | null => {
  const minutes = toTimeInMinutes(time);
  if (minutes == null) {
    return null;
  }

  const result = new Date(date);
  result.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return result;
};

const getWeekNumberFromYearStart = (date: Date): number => {
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const currentDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const dayOfYear = Math.floor((currentDay.getTime() - yearStart.getTime()) / millisecondsPerDay) + 1;
  return Math.ceil(dayOfYear / 7);
};

const getDeliveryScheduleWeekdayKey = (date: Date): string => {
  const day = date.getDay();
  return String(day === 0 ? 7 : day);
};

const isRecurringDeliveryWeekKey = (weekKey: string, weekNumber: number): boolean => {
  const recurringValue = Number(weekKey);
  if (!Number.isInteger(recurringValue) || recurringValue < 101) {
    return false;
  }

  if (recurringValue === 101) {
    return weekNumber % 2 === 1;
  }

  const interval = recurringValue - 100;
  return interval > 0 && weekNumber % interval === 0;
};

const getDeliveryWeekCandidateKeys = (
  weekMap: Record<string, unknown>,
  weekNumber: number,
): string[] => {
  const exactWeekKey = String(weekNumber);
  const candidateKeys: string[] = [];

  if (hasRecordKey(weekMap, exactWeekKey)) {
    candidateKeys.push(exactWeekKey);
  }

  Object.keys(weekMap)
    .filter((weekKey) => isRecurringDeliveryWeekKey(weekKey, weekNumber))
    .sort((left, right) => Number(left) - Number(right))
    .forEach((weekKey) => candidateKeys.push(weekKey));

  if (hasRecordKey(weekMap, '0')) {
    candidateKeys.push('0');
  }

  return candidateKeys;
};

const normalizeDeliveryScheduleDaySlots = (rawSlots: unknown): DeliveryScheduleDayMatch | null => {
  if (rawSlots == null) {
    return { allDay: true, slots: [] };
  }
  if (!Array.isArray(rawSlots)) {
    return null;
  }
  if (rawSlots.length === 0) {
    return { allDay: true, slots: [] };
  }

  const slots = rawSlots.filter((slot): slot is DeliveryScheduleTimeSlot => (
    Array.isArray(slot)
    && typeof slot[0] === 'string'
    && typeof slot[1] === 'string'
    && toTimeInMinutes(slot[0]) != null
    && toTimeInMinutes(slot[1]) != null
    && toTimeInMinutes(slot[0]) !== toTimeInMinutes(slot[1])
  ));

  return slots.length > 0 ? { allDay: false, slots } : null;
};

/**
 * Resolves the first applicable day schedule, honoring the backend priority of
 * exact year/week/day entries before recurring and zero-value fallbacks.
 */
export const getDeliveryScheduleDayMatch = (
  rawSchedule: unknown,
  date: Date,
): DeliveryScheduleDayMatch | null => {
  if (!isUnknownRecord(rawSchedule)) {
    return null;
  }

  const yearCandidateKeys = [String(date.getFullYear()), '0'].filter((yearKey, index, keys) => (
    keys.indexOf(yearKey) === index && hasRecordKey(rawSchedule, yearKey)
  ));
  const weekNumber = getWeekNumberFromYearStart(date);
  const weekdayKey = getDeliveryScheduleWeekdayKey(date);

  for (const yearKey of yearCandidateKeys) {
    const rawYearSchedule = rawSchedule[yearKey];
    if (!isUnknownRecord(rawYearSchedule)) {
      continue;
    }

    for (const weekKey of getDeliveryWeekCandidateKeys(rawYearSchedule, weekNumber)) {
      const rawWeekSchedule = rawYearSchedule[weekKey];
      if (!isUnknownRecord(rawWeekSchedule)) {
        continue;
      }

      for (const dayKey of [weekdayKey, '0']) {
        if (!hasRecordKey(rawWeekSchedule, dayKey)) {
          continue;
        }
        const match = normalizeDeliveryScheduleDaySlots(rawWeekSchedule[dayKey]);
        if (match) {
          return match;
        }
      }
    }
  }

  return null;
};

/**
 * Builds chronological configured intervals only. An absent schedule produces
 * no candidates by design; partner opening hours are not a fulfilment fallback.
 */
export const getScheduledIntervals = (
  rawSchedule: unknown,
  now: Date = new Date(),
  horizonDays: number = FULFILMENT_SCHEDULE_HORIZON_DAYS,
): ScheduledIntervalCandidate[] => {
  if (!isUnknownRecord(rawSchedule)) {
    return [];
  }

  const candidates: ScheduledIntervalCandidate[] = [];
  for (let dayOffset = 0; dayOffset < horizonDays; dayOffset += 1) {
    const date = new Date(now);
    date.setHours(0, 0, 0, 0);
    date.setDate(now.getDate() + dayOffset);
    const match = getDeliveryScheduleDayMatch(rawSchedule, date);
    if (!match) {
      continue;
    }

    const slots: DeliveryScheduleTimeSlot[] = match.allDay ? [['00:00', '23:59']] : match.slots;
    slots.forEach(([rawFrom, rawTo]) => {
      const from = normalizeTimeLabel(rawFrom);
      const to = normalizeTimeLabel(rawTo);
      const start = dateAtTime(date, from);
      const end = dateAtTime(date, to);
      if (!start || !end || start.getTime() === end.getTime()) {
        return;
      }
      if (end <= start) {
        end.setDate(end.getDate() + 1);
      }
      candidates.push({
        dateValue: formatDeliveryDateValue(start),
        label: `${from} - ${to}`,
        start,
        end,
      });
    });
  }

  return candidates.sort((left, right) => left.start.getTime() - right.start.getTime());
};

export const getEligibleScheduledIntervals = (
  rawSchedule: unknown,
  readinessTime: Date,
  now: Date = new Date(),
  isExactTimeAllowed: boolean = false,
): ScheduledIntervalCandidate[] => (
  getScheduledIntervals(rawSchedule, now)
    .filter((candidate) => (
      isExactTimeAllowed
        ? getCandidateExactTimeOptions(candidate, readinessTime).length > 0
        : candidate.start >= readinessTime
    ))
);

export const resolveAverageDeliveryMinutes = (value: unknown): number => (
  Number.isInteger(value) && Number(value) >= 0
    ? Number(value)
    : DEFAULT_AVERAGE_DELIVERY_MINUTES
);

export const getCartFulfilmentConstraints = (
  cart: CartProductReference[],
  menu: FoodMakerMenu,
): CartFulfilmentConstraints => cart.reduce<CartFulfilmentConstraints>((constraints, cartItem) => {
  const conditions = getProductDeliveryConditions(menu[cartItem.product_key]?.delivery_conditions);
  if (!conditions) {
    return constraints;
  }

  return {
    preparationMinutes: conditions.preparation_time != null
      ? Math.max(constraints.preparationMinutes, conditions.preparation_time)
      : constraints.preparationMinutes,
    nearestInterval: conditions.nearest_delivery_interval != null
      ? Math.max(constraints.nearestInterval ?? 0, conditions.nearest_delivery_interval)
      : constraints.nearestInterval,
  };
}, { preparationMinutes: 0, nearestInterval: null });

export const getProductFulfilmentConstraints = (
  value: ProductDeliveryConditions | null | undefined,
): CartFulfilmentConstraints => {
  const conditions = getProductDeliveryConditions(value);
  return {
    preparationMinutes: conditions?.preparation_time ?? 0,
    nearestInterval: conditions?.nearest_delivery_interval ?? null,
  };
};

export const calculateReadinessTime = (
  now: Date,
  constraints: CartFulfilmentConstraints,
  mode: FulfilmentMode,
  averageDeliveryTime: unknown,
): Date => {
  const readinessTime = new Date(now);
  readinessTime.setSeconds(0, 0);
  readinessTime.setMinutes(readinessTime.getMinutes() + constraints.preparationMinutes);
  if (mode === 'delivery' && constraints.preparationMinutes > 0) {
    readinessTime.setMinutes(readinessTime.getMinutes() + resolveAverageDeliveryMinutes(averageDeliveryTime));
  }
  return readinessTime;
};

/**
 * Produces the shared card/checkout resolution. `allowedIntervals` begins with
 * the one-based required interval and includes every later valid choice. Exact
 * time schedules retain an interval that has already started when at least one
 * option on its 15-minute grid still satisfies readiness.
 */
export const resolveFulfilmentSchedule = (
  rawSchedule: unknown,
  constraints: CartFulfilmentConstraints,
  mode: FulfilmentMode,
  averageDeliveryTime: unknown,
  now: Date = new Date(),
  isExactTimeAllowed: boolean = false,
): FulfilmentScheduleResolution => {
  const readinessTime = calculateReadinessTime(now, constraints, mode, averageDeliveryTime);
  const eligibleIntervals = getEligibleScheduledIntervals(
    rawSchedule,
    readinessTime,
    now,
    isExactTimeAllowed,
  );
  const firstAllowedIndex = Math.max(0, (constraints.nearestInterval ?? 1) - 1);
  const allowedIntervals = eligibleIntervals.slice(firstAllowedIndex);
  return {
    readinessTime,
    eligibleIntervals,
    firstAllowedInterval: allowedIntervals[0] ?? null,
    allowedIntervals,
  };
};

export const isCandidateSelection = (
  candidate: ScheduledIntervalCandidate,
  dateValue: string | null | undefined,
  intervalLabel: string | null | undefined,
): boolean => candidate.dateValue === dateValue && candidate.label === intervalLabel;

/**
 * Produces selectable clock times at 15-minute increments from the configured
 * interval start, optionally removing options before readiness. The duration
 * is capped at one day because backend slots can cross one midnight but cannot
 * represent multi-day fulfilment windows.
 */
export const getCandidateExactTimeOptions = (
  candidate: ScheduledIntervalCandidate,
  readinessTime?: Date | null,
): string[] => {
  const durationMinutes = Math.floor((candidate.end.getTime() - candidate.start.getTime()) / 60_000);
  if (durationMinutes < 0 || durationMinutes >= 24 * 60) {
    return [];
  }

  const optionCount = Math.floor(durationMinutes / 15) + 1;
  return Array.from({ length: optionCount }, (_, optionIndex) => {
    const optionTime = new Date(candidate.start);
    optionTime.setMinutes(optionTime.getMinutes() + (optionIndex * 15));
    return optionTime;
  })
    .filter((optionTime) => !readinessTime || optionTime >= readinessTime)
    .map((optionTime) => (
      `${String(optionTime.getHours()).padStart(2, '0')}:${String(optionTime.getMinutes()).padStart(2, '0')}`
    ));
};

/**
 * Exact times use the chosen interval as their authoritative boundary. For an
 * overnight interval, a clock value before the start belongs to the next day.
 */
export const getExactDateTimeForCandidate = (
  candidate: ScheduledIntervalCandidate,
  time: string,
): Date | null => {
  const selected = dateAtTime(candidate.start, time);
  if (!selected) {
    return null;
  }
  if (candidate.end.getDate() !== candidate.start.getDate() && selected < candidate.start) {
    selected.setDate(selected.getDate() + 1);
  }
  return selected;
};

export const isExactTimeWithinCandidate = (
  candidate: ScheduledIntervalCandidate,
  time: string | null | undefined,
  readinessTime: Date,
): boolean => {
  if (!time) {
    return false;
  }
  const selected = getExactDateTimeForCandidate(candidate, time);
  return Boolean(selected && selected >= candidate.start && selected <= candidate.end && selected >= readinessTime);
};

/**
 * Validates exact-time selections against both readiness and the configured
 * 15-minute grid. This prevents restored drafts from submitting arbitrary
 * minute values that the current dropdown cannot represent.
 */
export const isExactTimeOptionAvailable = (
  candidate: ScheduledIntervalCandidate,
  time: string | null | undefined,
  readinessTime: Date,
): boolean => {
  if (!time || !isExactTimeWithinCandidate(candidate, time, readinessTime)) {
    return false;
  }

  return getCandidateExactTimeOptions(candidate, readinessTime).includes(normalizeTimeLabel(time));
};
